import Lara from '@primeuix/themes/lara';
import {definePreset} from '@primeuix/themes';

/** Saga (light) uses the Material grey scale */
const sagaSurface = {
    'a': '#ffffff',
    'b': '#f8f9fa',
    'c': '#e9ecef',
    'd': '#dee2e6',
    'e': '#ffffff',
    'f': '#ffffff',
    'ground': '#bcc7d8',
    0:   '#ffffff',
    50:  '#FAFAFA',
    100: '#F5F5F5',
    200: '#EEEEEE',
    300: '#E0E0E0',
    400: '#BDBDBD',
    500: '#9E9E9E',
    600: '#757575',
    700: '#616161',
    800: '#424242',
    900: '#212121',
    950: '#121212',
};

/** Vela (dark) uses the navy/blue-grey scale */
const velaSurface = {
    'a': '#1f2d40',
    'b': '#17212f',
    'c': 'rgba(255, 255, 255, .03)',
    'd': '#304562',
    'e': '#1f2d40',
    'f': '#1f2d40',
    'ground': '#17212f',
    0:  '#e8e9ea',
    50: '#d1d3d5',
    100: '#b9bcc1',
    200: '#a2a6ac',
    300: '#8b9097',
    400: '#747a82',
    500: '#5d646d',
    600: '#454d59',
    700: '#304562',
    800: '#253144',
    900: '#1f2d40',
    950: '#17212f',
};

// ─── Primary palettes (exact from original theme CSS) ─────────────────────────

/** saga-blue  --primary-color: #2196F3 */
const sagaBlue = {
    50:  '#f4fafe',
    100: '#cae6fc',
    200: '#a0d2fa',
    300: '#75bef8',
    400: '#4baaf5',
    500: '#2196f3',
    600: '#1c80cf',
    700: '#1769aa',
    800: '#125386',
    900: '#0d3c61',
    950: '#071d2e',
};

/** saga-green  --primary-color: #4CAF50 */
const sagaGreen = {
    50:  '#f4fbf4',
    100: '#c3e6c4',
    200: '#93d194',
    300: '#62bc64',
    400: '#4caf50',
    500: '#3d8b40',
    600: '#357a38',
    700: '#2c6930',
    800: '#235828',
    900: '#1b4720',
    950: '#0d2310',
};

/** saga-orange  --primary-color: #FFC107 */
const sagaOrange = {
    50:  '#fffcf3',
    100: '#fff0c3',
    200: '#ffe494',
    300: '#ffd965',
    400: '#ffcd36',
    500: '#ffc107',
    600: '#d9a406',
    700: '#b38705',
    800: '#8c6a04',
    900: '#664d03',
    950: '#332602',
};

/** saga-purple  --primary-color: #9C27B0 */
const sagaPurple = {
    50:  '#faf4fb',
    100: '#e7cbec',
    200: '#d4a2dd',
    300: '#c279ce',
    400: '#af50bf',
    500: '#9c27b0',
    600: '#852196',
    700: '#6d1b7b',
    800: '#561561',
    900: '#3e1046',
    950: '#1f0823',
};

/** vela-blue  --primary-color: #64B5F6 */
const velaBlue = {
    50:  '#f7fbff',
    100: '#daedfd',
    200: '#bcdffb',
    300: '#9fd1f9',
    400: '#81c3f8',
    500: '#64b5f6',
    600: '#559ad1',
    700: '#467fac',
    800: '#376487',
    900: '#284862',
    950: '#142431',
};

/** vela-green  --primary-color: #81C784 */
const velaGreen = {
    50:  '#f9fcf9',
    100: '#e1f2e1',
    200: '#c9e7ca',
    300: '#b1dcb3',
    400: '#99d29b',
    500: '#81c784',
    600: '#6ea970',
    700: '#5a8b5c',
    800: '#476d49',
    900: '#345035',
    950: '#1a281a',
};

/** vela-orange  --primary-color: #FFD54F */
const velaOrange = {
    50:  '#fffdf6',
    100: '#fff5d5',
    200: '#ffedb3',
    300: '#ffe592',
    400: '#ffdd70',
    500: '#ffd54f',
    600: '#d9b543',
    700: '#b39537',
    800: '#8c752b',
    900: '#665520',
    950: '#332a10',
};

/** vela-purple  --primary-color: #BA68C8 */
const velaPurple = {
    50:  '#fcf7fc',
    100: '#eedbf2',
    200: '#e1bee7',
    300: '#d4a1dd',
    400: '#c785d2',
    500: '#ba68c8',
    600: '#9e58aa',
    700: '#82498c',
    800: '#66396e',
    900: '#4a2a50',
    950: '#251528',
};


const semanticColors = {
    'info-light': '#b3e5fc',
    'info-normal': '#0891cf',
    'info-dark': '#044868',
    'warn-light': '#ffecb3',
    'warn-normal': '#ca8a04',
    'warn-dark': '#986800',
    'error-light': '#ffcdd2',
    'error-normal': '#dc2626',
    'error-dark': '#ab1b1b',
    'success-light': '#c8e6c9',
    'success-normal': '#1db455',
    'success-dark': '#0e7936',
    'secondary-light': '#c8e6c9',
    'secondary-normal': '#9e9e9e',
    'secondary-dark': '#9e9e9e',
};


const createSagaPreset = (primary: any) => definePreset(Lara, {
    semantic: {
        primary: {...semanticColors, ...primary},
        colorScheme: {
            light: {
                surface: sagaSurface,
                highlight: {
                    color: '{primary.color}',
                },
                primary: {
                   contrastColor: '#fff'
                }
            }
        },
        formField: {
            borderRadius: '5px',
            paddingX: '0.45rem',
            paddingY: '0.45rem',
            lg: {
                paddingX: '0.75rem',
                paddingY: '0.75rem',
                fontSize: '1.2rem',
            },
            sm: {
                paddingX: '0.25rem',
                paddingY: '0.25rem',
                fontSize: '0.875rem',
            }
        }
    },
    components: {
        textarea: {
          colorScheme: {
              light: {
                  root: {
                      color: '{surface.950}',
                  }
              }
          }
        },
        accordion: {
            header: {
                borderRadius: '3px',
                first: {
                    topBorderRadius: '3px'
                },
                last: {
                    bottomBorderRadius: '3px'
                },
            },
            colorScheme: {
                light: {
                    header: {
                        color: '{surface.700}'
                    }
                }
            }
        },
        togglebutton: {
            root: {
                borderRadius: '3px',
                checkedBackground: '{primary.color}',
                checkedColor: '{primary.contrastColor}'
            },
            colorScheme: {
                light: {
                    root: {
                        checkedColor: '{primary.contrastColor}'
                    }
                }
            },
            icon: {
                checkedColor: '{primary.contrastColor}'
            }
        },
        toolbar: {
            colorScheme: {
                light: {
                    root: {
                        background: '{surface.900}',
                    }
                }
            }
        },
        button: {
            root: {
                borderRadius: '3px',
            },
            colorScheme: {
                light: {
                    root: {
                        secondary: {
                            background: '{slate.500}',
                            color: '{slate.50}',
                            hoverBackground: '{slate.600}',
                            hoverColor: '{slate.50}',
                            activeBackground: '{slate.600}',
                            activeColor: '{slate.50}',
                        },
                    }
                }
            }
        },
        panel: {
            colorScheme: {
                light: {
                    header: {
                        background: '{surface.b}',
                    },
                }
            }
        },
        datatable: {
            colorScheme: {
                light: {
                    row: {
                        stripedBackground: '{surface.100}',
                        hoverBackground: '{surface.300}',
                    },
                }
            }
        },
        tabs: {
            colorScheme: {
              light: {
                  tablist: {
                      background: '{surface.ground}',
                  },
                  tab: {
                  },
              }
            },
        },
        card: {
            root: {
                borderRadius: '3px',
            },
            colorScheme: {
                light: {
                    root: {
                        background: '{surface.700}',
                    },
                }
            }
        },
        message: {
            root: {
                borderWidth: '0'
            },
            colorScheme: {
                light: {
                    info: {
                        background: '{primary.info-light}',
                        color: '{primary.info-dark}',
                        borderColor: '{primary.info-normal}',
                    },
                    warn: {
                        background: '{primary.warn-light}',
                        color: '{primary.warn-dark}',
                        borderColor: '{primary.warn-normal}',
                    },
                    error: {
                        background: '{primary.error-light}',
                        color: '{primary.error-dark}',
                        borderColor: '{primary.error-normal}',
                    },
                    secondary: {
                        background: '{primary.secondary-light}',
                        color: '{primary.secondary-dark}',
                        borderColor: '{primary.secondary-normal}',
                    },
                    success: {
                        background: '{primary.success-light}',
                        color: '{primary.success-dark}',
                        borderColor: '{primary.success-normal}',
                    },
                }
            }
        },
        toast: {
            colorScheme: {
                light: {
                    info: {
                        background: '{primary.info-light}',
                        color: '{primary.info-dark}',
                        detailColor: '{primary.info-dark}',
                        borderColor: '{primary.info-normal}',
                    },
                    warn: {
                        background: '{primary.warn-light}',
                        color: '{primary.warn-dark}',
                        detailColor: '{primary.warn-dark}',
                        borderColor: '{primary.warn-normal}',
                    },
                    error: {
                        background: '{primary.error-light}',
                        color: '{primary.error-dark}',
                        detailColor: '{primary.error-dark}',
                        borderColor: '{primary.error-normal}',
                    },
                    secondary: {
                        background: '{primary.secondary-light}',
                        color: '{primary.secondary-dark}',
                        detailColor: '{primary.secondary-dark}',
                        borderColor: '{primary.secondary-normal}',
                    },
                    success: {
                        background: '{primary.success-light}',
                        color: '{primary.success-dark}',
                        detailColor: '{primary.success-dark}',
                        borderColor: '{primary.success-normal}',
                    },
                }
            }
        }
    }
});

const createVelaPreset = (primary: any) => definePreset(Lara, {
    semantic: {
        primary: {...semanticColors, ...primary},
        colorScheme: {
            dark: {
                surface: velaSurface,
                highlight: {
                    color: '{primary.color}',
                }
            },
        },
        formField: {
            borderRadius: '5px',
            paddingX: '0.45rem',
            paddingY: '0.45rem',
            lg: {
                paddingX: '0.75rem',
                paddingY: '0.75rem',
                fontSize: '1.2rem',
            },
            sm: {
                paddingX: '0.25rem',
                paddingY: '0.25rem',
                fontSize: '0.875rem',
            }
        }
    },
    components: {
        accordion: {
            header: {
                borderRadius: '3px',
                first: {
                    topBorderRadius: '3px'
                },
                last: {
                    bottomBorderRadius: '3px'
                },
            },
          colorScheme: {
              dark: {
                  header: {
                      color: '{surface.0}'
                  }
              }
          }
        },
        togglebutton: {
            root: {
                borderRadius: '3px',
                checkedBackground: '{primary.color}',
                checkedColor: '{primary.contrastColor}'
            },
            icon: {
                checkedColor: '{primary.contrastColor}'
            }
        },
        toolbar: {
            colorScheme: {
                dark: {
                    root: {
                        background: '{surface.900}',
                    }
                }
            }
        },
        button: {
            root: {
                borderRadius: '3px',

            },
            colorScheme: {
                dark: {
                    root: {
                        secondary: {
                            background: '{slate.500}',
                            color: '{slate.50}',
                            hoverBackground: '{slate.600}',
                            hoverColor: '{slate.50}',
                            activeBackground: '{slate.600}',
                            activeColor: '{slate.50}',
                        }
                    }
                }
            }
        },
        panel: {
            colorScheme: {
                dark: {
                    header: {
                        background: '{surface.900}',
                    },
                }
            }
        },
        datatable: {
            colorScheme: {
                dark: {
                    root: {
                        borderColor: '{surface.700}',
                    },
                    header: {
                        background: '{surface.900}',
                    },
                    headerCell: {
                        background: '{surface.900}',
                    },
                    row: {
                        background: '{surface.900}',
                        stripedBackground: '{surface.800}',
                        hoverBackground: '{surface.600}',
                        hoverColor: '{primary.300}',
                    },
                }
            }
        },
        tabs: {
            tablist: {
                background: '{surface.950}'
            }
        },
        card: {
            root: {
                borderRadius: '3px',
            },
            colorScheme: {
                dark: {
                    root: {
                        background: '{surface.700}',
                    }
                }
            }
        },
        message: {
          root: {
              borderWidth: '0'
          },
          colorScheme: {
              dark: {
                  info: {
                      background: '{primary.info-light}',
                      color: '{primary.info-dark}',
                      borderColor: '{primary.info-normal}',
                  },
                  warn: {
                      background: '{primary.warn-light}',
                      color: '{primary.warn-dark}',
                      borderColor: '{primary.warn-normal}',
                  },
                  error: {
                      background: '{primary.error-light}',
                      color: '{primary.error-dark}',
                      borderColor: '{primary.error-normal}',
                  },
                  secondary: {
                      background: '{primary.secondary-light}',
                      color: '{primary.secondary-dark}',
                      borderColor: '{primary.secondary-normal}',
                  },
                  success: {
                      background: '{primary.success-light}',
                      color: '{primary.success-dark}',
                      borderColor: '{primary.success-normal}',
                  },
              }
          }
        },
        toast: {
            colorScheme: {
                dark: {
                    info: {
                        background: '#b3e5fc',
                        color: '{button.info.color}',
                        detailColor: '{button.info.color}',
                        borderColor: '{button.info.borderColor}',
                    },
                    warn: {
                        background: '#ffecb3',
                        color: '{button.warn.color}',
                        detailColor: '{button.warn.color}',
                        borderColor: '{button.warn.borderColor}',
                    },
                    error: {
                        background: '#ffcdd2',
                        color: '{button.danger.color}',
                        detailColor: '{button.danger.color}',
                        borderColor: '{button.danger.borderColor}',
                    },
                    secondary: {
                        background: '#e1cfe7',
                        color: '{button.secondary.color}',
                        detailColor: '{button.secondary.color}',
                        borderColor: '{button.secondary.borderColor}',
                    },
                    success: {
                        background: '#c8e6c9',
                        color: '{button.success.color}',
                        detailColor: '{button.success.color}',
                        borderColor: '{button.success.borderColor}',
                    },
                }
            }
        }
    }
});

export const SagaBlue   = createSagaPreset(sagaBlue);
export const SagaGreen  = createSagaPreset(sagaGreen);
export const SagaOrange = createSagaPreset(sagaOrange);
export const SagaPurple = createSagaPreset(sagaPurple);

export const VelaBlue   = createVelaPreset(velaBlue);
export const VelaGreen  = createVelaPreset(velaGreen);
export const VelaOrange = createVelaPreset(velaOrange);
export const VelaPurple = createVelaPreset(velaPurple);
